import type { Notification } from './api'

export function getNotificationLink(notif: Notification): string {
  const data = notif.data || {}
  const appName = typeof data.app_name === 'string' && data.app_name.trim()
    ? data.app_name
    : undefined
  const appPath = appName ? `/apps/${encodeURIComponent(appName)}` : null
  const fallback = `/notifications?focus=${encodeURIComponent(String(notif.id))}`

  const feedbackId = typeof data.feedback_id === 'number' || typeof data.feedback_id === 'string'
    ? String(data.feedback_id)
    : null
  const feedbackPath = feedbackId
    ? `/feedback?item=${encodeURIComponent(feedbackId)}`
    : '/feedback'

  const listingFeedbackPath = appPath
    ? `${appPath}${feedbackId ? `?listingFeedback=${encodeURIComponent(feedbackId)}` : ''}#listing-feedback`
    : fallback

  switch (notif.type) {
    case 'app_shared':
    case 'access_approved':
      return appPath ?? '/apps?tab=shared'
    case 'access_denied':
      // A denied requester cannot open the app detail page. Take them to the
      // shared-apps view instead of knowingly routing into a 403/404 target.
      return '/apps?tab=shared'
    // Ownership transfer: the recipient acts from the My Apps banner; the
    // previous owner (accepted/declined/back-out) lands on the app if they can
    // still reach it, else My Apps.
    case 'app_transfer_request':
    case 'app_transfer_cancelled':
      return '/apps'
    case 'app_transfer_accepted':
    case 'app_transfer_declined':
      // The previous owner may have retained no access after an accepted
      // transfer, so the app detail route is not a safe destination.
      return '/apps'
    // Public-access lifecycle: all three land on the app detail page so an
    // admin sees the pending request inline (mirrors the owner-side
    // access_requests UX), and a requester lands on their own app's page to
    // see the approved/rejected state.
    case 'app_error':
    case 'other_app_errors':
    case 'app_auto_stopped':
    case 'app_storage_quota_warning':
    case 'app_storage_quota_exceeded':
    case 'admin_app_storage_quota_exceeded':
    case 'app_storage_over_quota_growth':
    case 'admin_app_storage_over_quota_growth':
    case 'production_build_triggered':
    case 'production_build_success':
    case 'production_build_failed':
    case 'other_app_published':
    case 'new_public_app':
    case 'access_request':
    case 'public_access_requested':
    case 'public_access_approved':
    case 'public_access_rejected':
      return appPath ?? fallback
    // Carry the work area through so the notification opens the session that
    // actually pinged, not whichever tab was open last. Without it Workspace
    // falls back to the per-app localStorage work area, which is the wrong tab
    // exactly when it matters — a background worktree finishing while you were
    // reading another one. Older notifications have no work_area; they keep
    // the previous last-tab behaviour rather than being forced to main.
    case 'claude_awaiting_input':
    case 'claude_task_complete': {
      if (!appName) return fallback
      const workArea = data.work_area as string | undefined
      const base = `/workspace?app=${encodeURIComponent(appName)}`
      return workArea ? `${base}&workarea=${encodeURIComponent(workArea)}` : base
    }
    case 'new_feedback':
    case 'feedback_comment':
    case 'feedback_status':
      return feedbackPath
    case 'app_store_feedback':
      return listingFeedbackPath
    case 'new_user_registered':
      return '/admin/users'
    case 'secret_scan_finding':
      return '/admin/secret-findings'
    case 'changelog_released':
      return '/changelog'
    // Deep-link to the dashboard with a query flag that AnnouncementProvider
    // detects (useAnnouncement) to open the survey modal, then strips.
    case 'survey_invite':
      return '/?announcement=survey'
    default:
      // A notification row must never look interactive, mark itself read, and
      // then do nothing. Unknown/legacy types open their focused entry on the
      // full notifications page, which is a stable and honest fallback.
      return fallback
  }
}
